import {
  ColDef,
  KeyCreatorParams,
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';

import { useCallback } from 'react';
import { get, merge } from 'lodash-es';
import {
  IFundDataMetricFieldsDataModel,
  IMetricsDataModel,
} from '../../../../../data-models/metrics.data-model';
import { useCompanyMapLookup } from '../../../../../services/hooks/useSaveCompanyData';
import { ICompanyDataModel } from '../../../../../data-models/company.data-model';
import { IField } from '../../../../../data-models/field2.data-model';
import { RendererType } from '../../../../../data-models/field-definition';
import { AgFormatterService } from '../../../../../util/ag-formatter-service';
import { toSingleDateString } from '../../../../../util/date-utilts';
import { StandardFormatterId } from '../../../../../util/formatter-service';
import { GridCompanyLinkRenderer } from './TypeBasedConfigs/TypeCellRenderers/ GridCompanyLinkRenderer';
import { usePortCoColDefs } from './portCoColDefs';
import { CompanyNameAndLogo } from './PredefinedColumns/CompanyNameAndLogo/CompanyNameAndLogo';

export function useColsWithCompanyData() {
  const getCompany = useCompanyMapLookup();
  const companyRendererParams = usePortCoColDefs();

  const setColDefsForCompanyData = useCallback(
    (columnProps: ColDef[], customFieldsById?: Map<number, IField<unknown>>) => {
      return columnProps.map((colProps) => {
        if (colProps.field && colProps.field.startsWith('fundCompany.customData.')) {
          const customFieldId = colProps.field.split('fundCompany.customData.')[1];

          const additionalDefs = getAdditionalCompanyColDefs(customFieldId, getCompany);
          const rendererType = customFieldsById?.get(Number(customFieldId))?.formMeta?.renderer?.type;
          // FD-3215: our valueGetter drops the column's formatter, so re-attach the
          // field's (by renderer type).
          const valueFormatter =
            rendererType && colProps.valueFormatter == null ? formatterForRenderer(rendererType) : undefined;
          return {
            ...colProps,
            valueGetter: (params: ValueGetterParams<IMetricsDataModel>) =>
              get(
                (params.data as unknown as IFundDataMetricFieldsDataModel)?._customFundFields,
                customFieldId
              ),
            ...(valueFormatter ? { valueFormatter } : {}),
            ...additionalDefs,
          };
        } else if (colProps.colId === CompanyNameAndLogo.NAME) {
          return merge({}, colProps, companyRendererParams);
        } else if (colProps.field && colProps.field.includes('company.')) {
          const companyField = colProps.field.split('company.')[1];

          const additionalDefs = getAdditionalCompanyColDefs(companyField, getCompany);
          // Same formatter drop as the fundCompany branch above, but scoped to date
          // custom fields: widening it would change how select fields already render.
          const rendererType = companyField.startsWith('customData.')
            ? customFieldsById?.get(Number(companyField.split('customData.')[1]))?.formMeta?.renderer?.type
            : undefined;
          const valueFormatter =
            rendererType === RendererType.date && colProps.valueFormatter == null
              ? formatterForRenderer(RendererType.date)
              : undefined;
          return {
            ...colProps,
            valueGetter: (params: ValueGetterParams<IMetricsDataModel>) => {
              const companyId = params.data?.companyId;
              const company = getCompany(companyId ?? -1);
              return get(company, companyField);
            },
            ...(valueFormatter ? { valueFormatter } : {}),
            ...additionalDefs,
          };
        } else {
          return colProps;
        }
      });
    },
    [companyRendererParams, getCompany]
  );

  return { setColDefsForCompanyData };
}

/**
 * A date custom field can hold a value that isn't a single date — the per-fund
 * concatenation `"10/29/2018,12/03/2021"` the shared date agg function is meant to
 * replace. The date formatter renders those as '', so the value would vanish; show it
 * as stored instead. Non-string values (nulls, group aggregates) go to the formatter
 * untouched so its group-row handling still applies.
 */
function formatterForRenderer(rendererType: RendererType | string): ValueFormatterFunc {
  const formatter = AgFormatterService.get().getFormatterForId(rendererType as StandardFormatterId);
  if (rendererType !== RendererType.date) {
    return formatter;
  }

  return (params) => {
    const value = params.value;
    if (typeof value !== 'string' || value === '') {
      return formatter(params);
    }
    const plainDate = toSingleDateString(value);
    return plainDate == null ? value : formatter({ ...params, value: plainDate });
  };
}

function getAdditionalCompanyColDefs(
  companyField: string,
  getCompany: (id: number) => ICompanyDataModel | null
): ColDef {
  if (companyField === 'website') {
    return getWebsiteColDefs(getCompany);
  }

  return {};
}

function getWebsiteColDefs(getCompany: (id: number) => ICompanyDataModel | null): ColDef {
  return {
    keyCreator: (params: KeyCreatorParams<IMetricsDataModel>) => {
      const companyId = params.data?.companyId;
      const company = getCompany(companyId ?? -1);
      return company?.website ?? '';
    },
    valueGetter: (params: ValueGetterParams<IMetricsDataModel>) => {
      const companyId = params.data?.companyId;
      return getCompany(companyId ?? -1);
    },
    cellRenderer: GridCompanyLinkRenderer,
    filter: 'agSetColumnFilter',
    filterParams: {
      filterValueGetter: (params: ValueGetterParams<IMetricsDataModel>) => {
        const companyId = params.data?.companyId;
        const company = getCompany(companyId ?? -1);
        return company?.website ?? '';
      },
      valueFormatter: (params: ValueFormatterParams<IMetricsDataModel, ICompanyDataModel>) =>
        params.value?.website?.replace(/^(https?:\/\/)?(www\.)?/, '') ?? '',
    },
  };
}
